// ============================================================================
// A/B testing — source de vérité CODE (miroir 1:1 de PostHog).
//
//   Chaque test A/B = 1 feature flag PostHog *multivarié* (Experiment).
//   Ce fichier centralise TOUTE la config pour créer/suivre le test :
//     • EXPERIMENTS      → descripteur (clé flag, variantes, métriques suivies)
//     • *_VARIANTS       → le contenu affiché par variante (à éditer à la main)
//     • FEATURE_FLAGS / DEFAULT_FLAG_VALUES → dérivés, ne pas éditer.
//
//   👉 Ajouter/modifier un test = éditer EXPERIMENTS + sa table de variantes,
//      puis répliquer la MÊME clé + les MÊMES variantes côté PostHog.
//   Doc lisible (hypothèse, décision) : ../AB-TESTS.md
// ============================================================================

// --- Descripteur d'expérience (mappe la config PostHog) --------------------
export type ExperimentConfig = {
  key: string; // clé du feature flag PostHog (identique code ↔ PostHog)
  description: string;
  status: 'draft' | 'running' | 'stopped';
  control: string; // clé de la variante de référence
  variantKeys: readonly string[]; // toutes les variantes (control inclus)
  primaryMetric: string; // event de conversion primaire (cf. TRACKING_EVENTS)
  secondaryMetrics: readonly string[]; // events secondaires (par étape d'entonnoir)
};

export const EXPERIMENTS = {
  // 2e tour. Le 1er (`home-copy`, #87871) a été conclu le 2026-07-31 :
  // `chat_agent` gagnant, servi à 100 % depuis. Il devient le control, face à
  // deux angles tirés du script VSL — les mêmes que sur /mc, pour comparer.
  // Primaire = clic CTA : à ce volume, `lead_qualified` ne tranchera pas.
  HOME_COPY: {
    key: 'home-copy-v2',
    description:
      "Angle complet de la copie Home, 2e tour : le gagnant `chat_agent` contre deux angles de la VSL (objectifs dépassés, « vous utilisez déjà l'IA »).",
    status: 'running',
    control: 'chat_agent',
    variantKeys: ['chat_agent', 'objectifs', 'dejateste'],
    primaryMetric: 'cta_click',
    secondaryMetrics: [
      'form_opened',
      'form_partial_submission',
      'lead_qualified',
      'application_submitted',
    ],
  },

  // --- Funnel masterclass (/mc) -------------------------------------------
  // Trois tests indépendants sur la même page. ⚠️ Les faire tourner
  // SÉQUENTIELLEMENT (headline d'abord) : lancés ensemble, 5 × 3 × 3 = 45
  // combinaisons, aucune n'atteindra la significativité sur ce volume.
  // Le CONTENU des variantes vit dans @/lib/masterclass (HEADLINES, TAGLINES,
  // CTAS) — ce fichier ne porte que le descripteur miroir de PostHog.
  MC_HEADLINE: {
    // 2e tour. Le 1er (`mc-headline`, 5 variantes) est arrêté : seul `equipe` se
    // détachait. Métrique primaire = clic CTA : c'est ce que le titre décide, et
    // à ~3 inscrits / 600 visiteurs, `seat_confirmed` ne trancherait jamais.
    key: 'mc-headline-v2',
    description:
      "Angle de la promesse sur la LP masterclass, 2e tour : le champion `equipe` contre trois angles tirés de la VSL (objectifs dépassés, « j'ai déjà testé », le lundi qui vous appartient) + l'angle direct « masterclass IA ».",
    status: 'running',
    control: 'equipe',
    variantKeys: ['equipe', 'objectifs', 'dejateste', 'lundi', 'masterclass'],
    primaryMetric: 'cta_click',
    secondaryMetrics: ['form_opened', 'form_partial_submission', 'lead_qualified', 'seat_confirmed'],
  },
  // Indépendant de `mc-headline-v2` (flags tirés séparément) : chaque test se
  // lit en marginalisant l'autre, sans croiser les combinaisons.
  MC_PROOF: {
    key: 'mc-preuve',
    description:
      "Preuve sociale sous le sous-titre de /mc : rien (la page actuelle) vs le chiffre (3 000 formés, 35 entreprises) vs les secteurs (environnements verrouillés, sans nom de client).",
    status: 'running',
    control: 'aucune',
    variantKeys: ['aucune', 'chiffres', 'secteurs'],
    primaryMetric: 'cta_click',
    secondaryMetrics: ['form_opened', 'form_partial_submission', 'lead_qualified', 'seat_confirmed'],
  },
  MC_TAGLINE: {
    key: 'mc-tagline',
    description:
      "Sous-titre de la LP masterclass : le chiffre de la répétition vs la preuve chiffrée vs l'angle contraintes/DSI.",
    status: 'draft',
    control: 'control',
    variantKeys: ['control', 'preuve', 'dsi'],
    primaryMetric: 'seat_confirmed',
    secondaryMetrics: ['cta_click', 'form_opened', 'form_partial_submission', 'lead_qualified'],
  },
  MC_CTA: {
    key: 'mc-cta',
    description:
      "Libellé du bouton d'inscription masterclass : neutre vs prix mis en avant vs créneau daté.",
    status: 'draft',
    control: 'reserver',
    variantKeys: ['reserver', 'gratuit', 'jeudi'],
    primaryMetric: 'seat_confirmed',
    secondaryMetrics: ['cta_click', 'form_opened', 'form_partial_submission'],
  },
} as const satisfies Record<string, ExperimentConfig>;

// --- Dérivés (ne pas éditer à la main) -------------------------------------
// Raccourci clés de flags, conservé pour la rétro-compat des imports.
export const FEATURE_FLAGS = {
  HOME_COPY: EXPERIMENTS.HOME_COPY.key,
  MC_HEADLINE: EXPERIMENTS.MC_HEADLINE.key,
  MC_TAGLINE: EXPERIMENTS.MC_TAGLINE.key,
  MC_PROOF: EXPERIMENTS.MC_PROOF.key,
  MC_CTA: EXPERIMENTS.MC_CTA.key,
} as const;

// Valeur par défaut si PostHog est bloqué (AdBlock) ou indisponible → control.
export const DEFAULT_FLAG_VALUES: Record<string, string> = {
  [EXPERIMENTS.HOME_COPY.key]: EXPERIMENTS.HOME_COPY.control,
  [EXPERIMENTS.MC_HEADLINE.key]: EXPERIMENTS.MC_HEADLINE.control,
  [EXPERIMENTS.MC_TAGLINE.key]: EXPERIMENTS.MC_TAGLINE.control,
  [EXPERIMENTS.MC_PROOF.key]: EXPERIMENTS.MC_PROOF.control,
  [EXPERIMENTS.MC_CTA.key]: EXPERIMENTS.MC_CTA.control,
};

// ============================================================================
// TEST `home-copy` — Variantes de la COPIE HOME
//   On teste l'ANGLE complet : bandeau + headline + sous-titre + paragraphe.
//   Le CTA, le badge vidéo et la copie du gate restent CONSTANTS (cf.
//   Landing.tsx / QualificationForm.tsx) pour isoler la variable testée.
//   Angles issus de ../marketing/angles.md (3 big ideas) + persona.md.
//
//   ⚠️ Les clés ci-dessous DOIVENT correspondre à EXPERIMENTS.HOME_COPY.variantKeys.
// ============================================================================

export type HomeVariant = {
  banner: string; // bandeau d'accroche (haut de page)
  headline: string;
  subheadline: string;
  body: string[]; // paragraphes sous la vidéo
};

export const HOME_VARIANTS: Record<string, HomeVariant> = {
  // Control — gagnant du 1er tour (`home-copy`, conclu le 2026-07-31).
  //   Les outils d'agents IA (namedropping, hyper-persona).
  chat_agent: {
    banner:
      "ChatGPT, Copilot, Claude… vous les utilisez déjà. Mais en simples chats ou en agents ?",
    headline:
      "Copilot, Claude, Cowork : transformez vos IA en agents qui font le travail à votre place.",
    subheadline:
      "Vous vous êtes arrêté au chat — poser une question, copier la réponse. La vraie bascule, c’est l’agent : une IA qui lit vos mails, ouvre vos Excel et génère vos livrables seule. Avec les outils que vous avez déjà — Microsoft 365, Copilot, Claude. Sans coder.",
    body: [
      "Un chat, vous le pilotez à chaque étape. Un agent — via Copilot, Claude ou Cowork — vous lui déléguez un objectif : « fais mon CR », et il le sort dans Word, structuré, avec décisions et responsables. C’est ce que 1 % des managers ont compris ces 3 derniers mois. La vidéo vous montre comment les configurer.",
    ],
  },
  // V2 — Le titre de la VSL : les objectifs dépassés. Chute (« n'atteignent
  //   plus »), puis bascule ; la vidéo livre le « comment ».
  objectifs: {
    banner:
      "Moins d’un manager sur cent délègue vraiment à l’IA. Ceux-là prennent de l’avance.",
    headline:
      "Certains managers n’atteignent plus leurs objectifs. Ils les dépassent.",
    subheadline:
      "Ils récupèrent une journée de travail par semaine en moins de deux mois — dans les outils que leur entreprise paie déjà, sans attendre leur DSI. Et ils la remettent dans ce qui compte : leurs chiffres, leur équipe, leur trajectoire.",
    body: [
      "Mêmes réunions, mêmes reportings, même équipe. Ils ont changé une seule chose, et leurs collègues commencent à leur demander comment ils font. La vidéo vous montre laquelle.",
    ],
  },
  // V3 — L'objection « j'ai déjà testé », retournée en accroche. L'ennemi
  //   commun : les démos faites ailleurs, sans DSI ni données sensibles.
  dejateste: {
    banner:
      "Vous avez essayé ChatGPT ou Copilot, et votre semaine n’a pas changé ? La méthode était faite pour quelqu’un d’autre.",
    headline:
      "Vous utilisez déjà l’IA. Et votre semaine n’a pas bougé d’une heure.",
    subheadline:
      "On vous a montré l’IA sur la machine de quelqu’un d’autre : sans DSI, sans données sensibles, sans directeur qui attend son reporting vendredi. Voici comment elle travaille chez vous — dans vos outils, avec vos contraintes. Une journée récupérée par semaine.",
    body: [
      "Vous avez appris à écrire plus vite. Or écrire a toujours été la partie facile. Ce qui vous coûte, c’est d’aller rechercher une information qui existait déjà, pour la remettre dans un format que vous refaites à l’identique. La vidéo vous montre où casser ce cycle.",
    ],
  },
};
